import React, { useEffect, useRef } from 'react';
import AnalyticsWebClient, {
  envType,
  originType,
  platformType,
  tenantType,
} from '@atlassiansox/analytics-web-client';
import { GlobalPageLoadExperience, payloadPublisher, UFOExperience } from '@atlassian/ufo';
import * as Sentry from '@sentry/react';

import { isRunningTests } from '@/utils';

// Mostly copied from onesight-ui
// https://bitbucket.org/atlassian/onesight-ui/src/master/libs/onesight-ui/analytics/src/lib/publisher.ts
const gasV3ProductName = 'tome'; // case sensitive and must match the product name in GASv3
const productVersion = String(__BUILD_DATE__);
let analyticsClientInstance: AnalyticsWebClient;

const env = (() => {
  switch (__DEPLOY_ENV__) {
    case 'production':
      return envType.PROD;
    case 'staging':
      return envType.STAGING;
    case 'development':
      return envType.DEV;
    default:
      return envType.LOCAL;
  }
})();

const initAnalyticsClient = (): AnalyticsWebClient => {
  analyticsClientInstance = new AnalyticsWebClient(
    {
      env,
      product: gasV3ProductName,
      version: productVersion,
      origin: originType.WEB,
      platform: platformType.WEB,
      locale: navigator.language ?? 'en-US',
    },
    {
      // https://atlassian.slack.com/archives/CFG85MPMX/p1651473773042159?thread_ts=1651472097.518889&cid=CFG85MPMX
      useStargate: false,
    }
  );
  analyticsClientInstance.setTenantInfo(tenantType.NONE);
  return analyticsClientInstance;
};

export const getAnalyticsClient = () => {
  if (analyticsClientInstance) return analyticsClientInstance;
  // eslint-disable-next-line no-console
  console.warn('Analytics Client not initialised');
  return null;
};

const initUFO = (analyticsClient: AnalyticsWebClient) => {
  payloadPublisher.setup({
    gasv3: analyticsClient,
    product: gasV3ProductName,
    app: {
      version: { web: productVersion },
    },
  });
};

export function useInitializedAnalytics() {
  useEffect(() => {
    try {
      if (!isRunningTests()) {
        const client = initAnalyticsClient();
        initUFO(client);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to initialise analytics', error);
      Sentry.captureException(error);
    }
    // Assume isRunningTests() is immutable per browser environment
  }, []);
}

/**
 * Abort an experience if it's not in an initial or final state.
 *
 * @param exp The experience to abort.
 */
export const abortExperienceIfNonFinal = (exp: UFOExperience) => {
  if (
    // Not in any initial or final states
    exp.state.final !== true
  ) {
    // transition from started to started is not allowed
    // abort any existing experience in progress to ensure we can start again
    // noinspection JSIgnoredPromiseFromCall
    exp.abort();
  }
};

/**
 * Abort an experience if it's not in an initial or final state, and start it. Useful
 * because an experience can't be started unless it's in a final state.
 *
 * @param exp The experience to start.
 */
export const startExperienceAfterAbort = (exp: UFOExperience) => {
  abortExperienceIfNonFinal(exp);
  // noinspection JSIgnoredPromiseFromCall
  exp.start();
};

// A map of sequence numbers of experience starts.
// Used to ensure ExperienceSuccess component gets re-rendered for distinct
// experience starts, and to ensure cleanup doesn't happen for starts that happen
// after cleanup registration.
// NOTE: Assumes that no two experiences exist for a page - does not support
// ConcurrentExperience.
const expToSeq = new Map();

const getSeq = (exp: UFOExperience): number => {
  if (expToSeq.has(exp)) {
    return expToSeq.get(exp);
  }

  expToSeq.set(exp, 0);
  return 0;
};

// Returns the current value and increments.
const incrementSeq = (exp: UFOExperience): number => {
  const v = getSeq(exp);
  expToSeq.set(exp, v + 1);
  return v;
};

// Useful to look at the source code
// https://bitbucket.org/atlassian/atlassian-frontend/src/master/packages/data/ufo-experimental/src/platform-client/core/experience/

/**
 * Restart a UFO experience without being required to remount the component, based
 * on a dependency list.
 *
 * If the dependency list changes, or upon initial mount, or if the experience we are measuring
 * has changed, then the old experience will be aborted if not in a final state and
 * the new experience will be started.
 *
 * Useful if the component itself has state such as pagination.
 *
 * @param exp The experience to start.
 * @param deps The dependency list. If any objects change, a restart is triggered.
 * @return number A sequence number starting from 0 that increases every time the experience
 * is started. Used so that the experience success component can be re-rendered for
 * each experience measurement.
 */
export const useRestartUFOComponentExperience = (exp: UFOExperience, deps: any[]): number => {
  const depsWithExp = [exp, ...deps];
  const depsPrevRef = useRef<any[] | null>(null);
  const depsPrev = depsPrevRef.current;

  useEffect(() => {
    const lastSeq = getSeq(exp); // Captured after render
    return () => {
      const curSeq = getSeq(exp); // Get at cleanup time
      // Check that the clean up effect is running for an experience that
      // was not just started in render. If it was just rendered, we don't want to abort
      // it immediately after due to a dangling cleanup.
      if (curSeq <= lastSeq) {
        // noinspection JSIgnoredPromiseFromCall
        abortExperienceIfNonFinal(exp);
      }
    };
    // If the experience changes, it will be aborted by the cleanup registered from the
    // old hook use. Same effect if we are unmounted.
  }, [exp]);

  if (depsPrev === null) {
    // We've never been mounted before
    depsPrevRef.current = depsWithExp;
    startExperienceAfterAbort(exp);
    return incrementSeq(exp);
  }

  if (depsPrev.length !== depsWithExp.length) {
    // Invalid hook usage, don't overwrite ref because we didn't do anything
    throw new Error('Hook called with different number of dependencies');
  }

  for (let i = 0; i < depsWithExp.length; ++i) {
    // Shallow pointer comparison of objects
    if (!Object.is(depsPrev[i], depsWithExp[i])) {
      depsPrevRef.current = depsWithExp;
      startExperienceAfterAbort(exp);
      return incrementSeq(exp);
    }
  }

  // Do nothing if dependencies haven't changed
  return getSeq(exp);
};

/**
 * React context that holds the nearest experience up the render tree.
 *
 * Used so that useFetchErrorController's async callbacks
 * have a reference to the current experience in progress via closure capture.
 *
 * The default is the global page load experience.
 *
 * Not used to trigger re-renders, just to pass the experience value down the tree. Hence
 * no modifier function is provided to the descendants.
 */
export const UFOExperienceContext = React.createContext<UFOExperience>(GlobalPageLoadExperience);
UFOExperienceContext.displayName = 'UFOExperienceContext';
